/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { Auth } from '../../../modules/auth/Auth';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Filter from '../../../interfaces/forms/Filter';
import { useDispatch } from 'react-redux';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { configDefault } from '../../../core/config';
import Loader from '../../../components/Loaders/Loader';
import { defaultActions } from '../../../core/actionsDefault';
import Order from '../../../interfaces/forms/Order';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import TableBody from '@material-ui/core/TableBody';
import TableFooter from '@material-ui/core/TableFooter';
import Pager from '../../../components/Adescom/PagerRange';
import { makeStyles, Table, withStyles } from '@material-ui/core';
import ReportListItem from './ReportListItem';
import ViewDialog from '../../../components/Dialog/ViewDialog';
import ReportView from './ReportView';
import AlertInfo from '../../../components/Info/AlertInfo';
import { reportConstants } from '../../../modules/reports/actions';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import PlatformResellerInterface from "../../../interfaces/platform/PlatformResellerInterface";
import {PlatformResellerCollectionInterface} from "../../../interfaces/collections/PlatformResellerCollectionInterface";
import {NotificationHelper} from "../../../helpers/NotificationHelper";

const ReportList = (props: { classes: any }): React.ReactElement => {
    const dispatch = useDispatch();

    const [items, setItems] = useState<any>(null);
    const [filters, setFilters] = useState<Array<Filter>>([
        { name: 'platformUserId', value: Auth.getUserId() },
        { name: 'deleted', value: false },
    ]);
    const [orders, setOrders] = useState<Array<Order>>([{ name: 'id', value: 'desc' }]);
    const [visibleLoader, setVisibleLoader] = useState<boolean>(false);
    const [activeRow, setActiveRow] = useState<any>(null);
    const [openReport, setOpenReport] = useState<boolean>(false);
    const [report, setReport] = useState<any>(null);
    const [deleteParams, setDeleteParams] = useState<any>(null);
    const [alert, setAlert] = useState<any>(null);
    const [platformResellerList, setPlatformResellerList] = useState<Array<PlatformResellerInterface> | null>(null);
    const [translations, setTranslations] = useState<Array<{name: string, value: Array<any>}>>([]);

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);

    const getCSVList = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath('/reports', configDefault.PLATFORM_API_PREFIX);
            }
            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                reportConstants.REPORT_LIST,
                filters,
                orders,
                (data: any) => {
                    setItems(data);
                    setVisibleLoader(false);
                },
                {
                    dispatchAfterCallback: false,
                    propagateDataToCallback: true,
                }
            );
        },
        [dispatch, filters, orders]
    );

    const deleteReportConfirm = (params: { id: number }): void => {
        setAlert(
            <AlertInfo
                onConfirm={(): void => deleteReport(params)}
                onCancel={cancelDelete}
                confirmBtnText={'reports:h.delete_report'}
                cancelBtnText={'general:common.cancel'}
                content="reports:h.delete_report_text"
            />
        );
    };

    const cancelDelete = (): void => {
        setAlert(null);
        setDeleteParams(null);
    };

    const deleteReport = (deleteParams: { id: number }): void => {
        if (deleteParams !== null) {
            dispatch(
                defaultActions.remove(
                    FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/reports/' + deleteParams.id, configDefault.PLATFORM_API_PREFIX)),
                    reportConstants.REPORT_DELETE,
                    {
                        onSuccess: (data: any) => {
                            cancelDelete();
                            getCSVList();
                            NotificationHelper.notifySuccess('reports:notify.delete_success', dispatch);
                        },
                        onError: (err: any) => {
                            NotificationHelper.notifyError('reports:notify.delete_error', dispatch);
                        }
                    }
                )
            );
        } else {
            cancelDelete();
        }
    };

    const setActiveRowHandler = (id: number): void => {
        if (activeRow === id) {
            setActiveRow(null);
        } else {
            setActiveRow(id);
        }
    };

    const openReportView = (item: any): void => {
        setOpenReport(true);
        setReport(item);
    };

    const closeReportView = (): void => {
        setOpenReport(false);
        setReport(null);
    };

    const paginationCallbackFunction = (uri: string): void => {
        setVisibleLoader(true);
        getCSVList(uri);
    };

    const getPlatformResellerList = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath('/platform_resellers', configDefault.PLATFORM_API_PREFIX);
            }
            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
               'platform_reseller_get',
                [],
                [],
                (data: PlatformResellerCollectionInterface) => {
                    if(data && data['hydra:member']) {
                        setPlatformResellerList(data['hydra:member']);
                        setTranslations([...translations, {name: 'platformResellerId', value: data['hydra:member']}]);
                    }
                },
                {
                    dispatchAfterCallback: false,
                    propagateDataToCallback: true,
                }
            );
        },
        [dispatch,translations]
    );

    useEffect(() => {
        getCSVList();
    }, [getCSVList]);

    useEffect(() => {
        if(!platformResellerList) {
            getPlatformResellerList();
        }
    },[getPlatformResellerList,platformResellerList])

    const tableStickyHeader = { root: props.classes.tableStickyHeader };
    const tableCellRoot = { root: props.classes.tableCellRoot };
    const tableStickyCommand = { root: props.classes.tableStickyCommand };
    return (
        <React.Fragment>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell classes={tableStickyCommand} padding="none" align="left">
                            <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="left" title="reports:h.id" />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="left" title="reports:h.partner" />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="left" title="reports:h.type" />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="left" title="reports:h.created_at" />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="left" title="reports:h.started_process_at" />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="left" title="reports:h.processed_at" />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="left" title="reports:h.state" />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align="left">
                            <TableHeaderContentCell textAlign="center" title="reports:h.progress" />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items && platformResellerList &&
                        items['hydra:member'] &&
                        items['hydra:member'].map((item: any): any => {
                            return (
                                <ReportListItem
                                    item={item}
                                    key={'client_balance_' + item.id}
                                    activeRow={activeRow}
                                    setActiveRow={setActiveRowHandler}
                                    openReportView={openReportView}
                                    deleteCallback={deleteReportConfirm}
                                    platformResellerList={platformResellerList}
                                />
                            );
                        })}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell style={{ backgroundColor: 'white' }} classes={tableCellRoot} colSpan={25}>
                            {items && items['hydra:view'] && <Pager callback={paginationCallbackFunction} views={items} />}
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
            {platformResellerList &&
                <ViewDialog
                    maxWidth="sm"
                    fullWidth={true}
                    title={'reports:h.report_view'}
                    content={<ReportView item={report} translations={translations}/>}
                    agreeButtonName={'general:common.close'}
                    open={openReport}
                    callBackAgree={closeReportView}
                />
            }
            {alert}
            <Loader visible={visibleLoader} />
        </React.Fragment>
    );
};
export default withStyles(oplAppStyle)(ReportList);
